import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Loader2, Mail } from "lucide-react";
import { COMPANY } from "@/lib/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { id: "facciate", label: "Rifacimento Facciate" },
  { id: "cappotto", label: "Cappotto Termico" },
  { id: "finiture", label: "Finiture d'Interni" },
  { id: "ristrutturazioni", label: "Ristrutturazione chiavi in mano" },
  { id: "carpenteria", label: "Carpenteria & Arredo" },
  { id: "fotovoltaico", label: "Impianti Fotovoltaici" },
  { id: "altro", label: "Altro / Sopralluogo" },
];

const TIMING = [
  { id: "urgente", label: "Urgente (entro 30 giorni)" },
  { id: "1-3-mesi", label: "Entro 1-3 mesi" },
  { id: "3-6-mesi", label: "Entro 3-6 mesi" },
  { id: "da-valutare", label: "Sto valutando" },
];

const INITIAL = {
  nome: "",
  cognome: "",
  email: "",
  telefono: "",
  citta: "",
  servizio: "",
  tempistica: "",
  messaggio: "",
  privacy: false,
};

export default function QuoteForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => {
    if (step === 1 && !data.servizio) {
      toast.error("Seleziona un servizio");
      return;
    }
    if (step === 2 && !data.tempistica) {
      toast.error("Seleziona una tempistica");
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.privacy) {
      toast.error("Devi accettare la privacy policy");
      return;
    }
    if (!data.nome || !data.cognome || !data.email || !data.telefono) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/preventivi`, data);
      setDone(true);
      toast.success("Richiesta inviata! Ti contatteremo a breve.");
    } catch (err) {
      console.error(err);
      toast.error("Errore nell'invio. Riprova o chiama il numero in pagina.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section
        id="preventivo"
        data-testid="quote-form-section"
        className="relative bg-[#050B14] py-28 md:py-40"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div
            data-testid="quote-success"
            className="w-20 h-20 bg-[#FF5A1F] mx-auto flex items-center justify-center mb-8"
          >
            <Check size={40} className="text-white" strokeWidth={3} />
          </div>
          <h2 className="headline text-white text-4xl md:text-6xl">
            Richiesta{" "}
            <span className="font-display italic font-medium text-slate-300">
              ricevuta.
            </span>
          </h2>
          <p className="mt-6 text-slate-400 text-lg leading-relaxed">
            Grazie {data.nome}, abbiamo registrato la tua richiesta. Un nostro
            referente ti contatterà entro 24 ore lavorative al numero indicato
            per fissare il sopralluogo.
          </p>
          <button
            onClick={() => {
              setDone(false);
              setData(INITIAL);
              setStep(1);
            }}
            data-testid="quote-reset-btn"
            className="mt-10 inline-flex items-center gap-2 border border-slate-600 hover:border-white text-white px-6 py-3 text-sm font-semibold"
          >
            Invia un'altra richiesta
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id="preventivo"
      data-testid="quote-form-section"
      className="relative bg-[#050B14] py-28 md:py-40 grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left - text */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#FF5A1F]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#FF5A1F] font-bold">
              Preventivo Gratuito
            </span>
          </div>
          <h2 className="headline text-white text-4xl md:text-6xl">
            Parlaci del tuo{" "}
            <span className="font-display italic font-medium text-slate-300">
              progetto.
            </span>
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
            Compila il modulo: ti richiamiamo entro 24h per fissare un
            sopralluogo gratuito e senza impegno.
          </p>

          <a
            href={`mailto:${COMPANY.quotesEmail}`}
            data-testid="preventivi-email-link"
            className="mt-6 inline-flex items-center gap-3 border border-slate-700 hover:border-[#FF5A1F] px-4 py-3 group transition-colors"
          >
            <Mail size={16} className="text-[#FF5A1F]" />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
                Oppure scrivici direttamente
              </div>
              <div className="text-white text-sm font-bold mt-0.5 group-hover:text-[#FF5A1F] transition-colors">
                {COMPANY.quotesEmail}
              </div>
            </div>
          </a>

          <div className="mt-12 space-y-6">
            {[
              { n: "01", t: "Tipo di lavoro" },
              { n: "02", t: "Tempistiche" },
              { n: "03", t: "I tuoi contatti" },
            ].map((s, i) => (
              <div
                key={s.n}
                className={`flex items-center gap-5 transition-opacity ${
                  step === i + 1
                    ? "opacity-100"
                    : step > i + 1
                    ? "opacity-50"
                    : "opacity-30"
                }`}
              >
                <div
                  className={`w-10 h-10 flex items-center justify-center font-mono text-sm font-bold ${
                    step > i + 1
                      ? "bg-[#FF5A1F] text-white"
                      : step === i + 1
                      ? "bg-white text-[#050B14]"
                      : "bg-[#111C30] text-slate-500"
                  }`}
                >
                  {step > i + 1 ? <Check size={16} strokeWidth={3} /> : s.n}
                </div>
                <span className="text-white font-semibold">{s.t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right - form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="bg-[#0A1120] border border-[#1E293B] p-6 md:p-10"
          >
            <div className="flex items-center justify-between mb-8">
              <div className="font-mono text-xs tracking-[0.25em] text-slate-500 uppercase">
                Step {step} / 3
              </div>
              <div className="flex gap-1 w-32">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 ${
                      s <= step ? "bg-[#FF5A1F]" : "bg-slate-700"
                    }`}
                  />
                ))}
              </div>
            </div>

            {step === 1 && (
              <div data-testid="step-1">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
                  Quale servizio ti interessa?
                </h3>
                <p className="text-slate-400 mb-8 text-sm">
                  Seleziona la categoria principale del tuo intervento.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SERVICES.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`service-option-${s.id}`}
                      onClick={() => update("servizio", s.id)}
                      className={`text-left p-4 border transition-all ${
                        data.servizio === s.id
                          ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white"
                          : "border-slate-700 hover:border-slate-500 text-slate-300"
                      }`}
                    >
                      <div className="font-semibold text-sm">{s.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-2">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
                  Quando vorresti iniziare?
                </h3>
                <p className="text-slate-400 mb-8 text-sm">
                  Indicativo, ci aiuta a pianificare meglio.
                </p>
                <div className="grid gap-3">
                  {TIMING.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      data-testid={`timing-option-${t.id}`}
                      onClick={() => update("tempistica", t.id)}
                      className={`text-left p-4 border transition-all ${
                        data.tempistica === t.id
                          ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white"
                          : "border-slate-700 hover:border-slate-500 text-slate-300"
                      }`}
                    >
                      <div className="font-semibold text-sm">{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-3">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-tight">
                  I tuoi contatti
                </h3>
                <p className="text-slate-400 mb-8 text-sm">
                  Ti contattiamo entro 24h lavorative.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <input
                    data-testid="input-nome"
                    type="text"
                    placeholder="Nome*"
                    value={data.nome}
                    onChange={(e) => update("nome", e.target.value)}
                    className="dea-input"
                    required
                  />
                  <input
                    data-testid="input-cognome"
                    type="text"
                    placeholder="Cognome*"
                    value={data.cognome}
                    onChange={(e) => update("cognome", e.target.value)}
                    className="dea-input"
                    required
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <input
                    data-testid="input-email"
                    type="email"
                    placeholder="Email*"
                    value={data.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="dea-input"
                    required
                  />
                  <input
                    data-testid="input-telefono"
                    type="tel"
                    placeholder="Telefono*"
                    value={data.telefono}
                    onChange={(e) => update("telefono", e.target.value)}
                    className="dea-input"
                    required
                  />
                </div>
                <input
                  data-testid="input-citta"
                  type="text"
                  placeholder="Città / località intervento"
                  value={data.citta}
                  onChange={(e) => update("citta", e.target.value)}
                  className="dea-input mb-3"
                />
                <textarea
                  data-testid="input-messaggio"
                  placeholder="Descrivi brevemente il progetto (opzionale)"
                  rows={4}
                  value={data.messaggio}
                  onChange={(e) => update("messaggio", e.target.value)}
                  className="dea-input"
                />
                <label className="flex items-start gap-3 mt-5 cursor-pointer">
                  <input
                    data-testid="input-privacy"
                    type="checkbox"
                    checked={data.privacy}
                    onChange={(e) => update("privacy", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#FF5A1F]"
                  />
                  <span className="text-xs text-slate-400 leading-relaxed">
                    Accetto il trattamento dei dati personali ai sensi del GDPR
                    (Reg. UE 2016/679) per essere ricontattato relativamente
                    alla richiesta.
                  </span>
                </label>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-800">
              {step > 1 ? (
                <button
                  type="button"
                  data-testid="form-back-btn"
                  onClick={back}
                  className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-semibold"
                >
                  <ArrowLeft size={16} /> Indietro
                </button>
              ) : (
                <span />
              )}
              {step < 3 ? (
                <button
                  type="button"
                  data-testid="form-next-btn"
                  onClick={next}
                  className="inline-flex items-center gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] text-white px-6 py-3.5 text-sm font-semibold transition-colors"
                >
                  Continua <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  type="submit"
                  data-testid="form-submit-btn"
                  disabled={loading}
                  className="inline-flex items-center gap-3 bg-[#FF5A1F] hover:bg-[#EA450C] disabled:opacity-60 text-white px-6 py-3.5 text-sm font-semibold transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Invio…
                    </>
                  ) : (
                    <>
                      Invia richiesta <ArrowRight size={18} />
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
